/**
 * resilience.js — API Resilience Layer
 * Circuit breaker, retry with exponential backoff, and graceful degradation
 *
 * Phase 5 hardening: wraps all external API calls with consistent error handling.
 *
 * Exports:
 *   - CircuitBreaker: per-service circuit breaker (closed → open → half-open)
 *   - fetchWithRetry: fetch wrapper with configurable retry + backoff
 *   - breakers: pre-configured circuit breaker instances for each API source
 */

// ===== CIRCUIT BREAKER =====

/**
 * Circuit Breaker States:
 *   CLOSED   — normal operation, requests pass through
 *   OPEN     — too many failures, requests short-circuited (fail fast)
 *   HALF_OPEN — cooldown expired, allow ONE probe request to test recovery
 *
 * Config:
 *   failThreshold  — consecutive failures before opening (default: 3)
 *   cooldownMs     — time in OPEN state before trying HALF_OPEN (default: 60s)
 *   successThreshold — consecutive successes in HALF_OPEN before closing (default: 1)
 */
export class CircuitBreaker {
  constructor(name, opts = {}) {
    this.name = name;
    this.failThreshold = opts.failThreshold ?? 3;
    this.cooldownMs = opts.cooldownMs ?? 60000;
    this.successThreshold = opts.successThreshold ?? 1;

    this._state = 'CLOSED';
    this._failCount = 0;
    this._successCount = 0;
    this._lastFailTime = 0;
    this._listeners = [];
  }

  get state() { return this._state; }

  /** Register a state-change listener: fn(newState, breakerName) */
  onStateChange(fn) { this._listeners.push(fn); }

  _emit(newState) {
    this._state = newState;
    this._listeners.forEach(fn => {
      try { fn(newState, this.name); } catch (_) {}
    });
  }

  /**
   * Can a request pass through?
   * Returns true for CLOSED and HALF_OPEN (probe), false for OPEN (unless cooldown expired).
   */
  canRequest() {
    if (this._state === 'CLOSED') return true;
    if (this._state === 'HALF_OPEN') return true;
    // OPEN — check cooldown
    if (Date.now() - this._lastFailTime >= this.cooldownMs) {
      this._emit('HALF_OPEN');
      this._successCount = 0;
      console.log(`[CB:${this.name}] Cooldown expired → HALF_OPEN (probe)`);
      return true;
    }
    return false;
  }

  /** Record a successful call */
  recordSuccess() {
    if (this._state === 'HALF_OPEN') {
      this._successCount++;
      if (this._successCount >= this.successThreshold) {
        this._failCount = 0;
        this._emit('CLOSED');
        console.log(`[CB:${this.name}] Recovery confirmed → CLOSED`);
      }
    } else {
      this._failCount = 0; // reset consecutive failures on any success
    }
  }

  /** Record a failed call */
  recordFailure() {
    this._failCount++;
    this._lastFailTime = Date.now();

    if (this._state === 'HALF_OPEN') {
      // Probe failed — back to OPEN
      this._emit('OPEN');
      console.warn(`[CB:${this.name}] Probe failed → OPEN (cooldown: ${this.cooldownMs / 1000}s)`);
    } else if (this._failCount >= this.failThreshold) {
      this._emit('OPEN');
      console.warn(`[CB:${this.name}] ${this._failCount} failures → OPEN (cooldown: ${this.cooldownMs / 1000}s)`);
    }
  }

  /** Force reset to CLOSED (e.g., user action / settings change) */
  reset() {
    this._failCount = 0;
    this._successCount = 0;
    this._lastFailTime = 0;
    this._emit('CLOSED');
  }

  /** Summary for diagnostics / UI */
  status() {
    return {
      name: this.name,
      state: this._state,
      failCount: this._failCount,
      lastFailTime: this._lastFailTime,
      cooldownMs: this.cooldownMs
    };
  }
}

// ===== RETRY WITH BACKOFF =====

/**
 * Fetch wrapper with retry + exponential backoff
 *
 * @param {string} url
 * @param {Object} opts
 * @param {number} opts.timeout     — per-request timeout in ms (default: 10000)
 * @param {number} opts.retries     — max retry attempts (default: 2, so 3 total)
 * @param {number} opts.backoffBase — base delay in ms (default: 1000)
 * @param {number} opts.backoffMax  — max delay in ms (default: 8000)
 * @param {CircuitBreaker} opts.breaker — optional circuit breaker instance
 * @param {Function} opts.onRetry   — optional callback(attempt, error, delayMs)
 * @returns {Response} — fetch Response object
 * @throws {Error} — after all retries exhausted or circuit breaker open
 */
export async function fetchWithRetry(url, opts = {}) {
  const {
    timeout = 10000,
    retries = 2,
    backoffBase = 1000,
    backoffMax = 8000,
    breaker = null,
    onRetry = null,
    ...fetchOpts
  } = opts;

  const maxAttempts = retries + 1;

  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    // Circuit breaker check
    if (breaker && !breaker.canRequest()) {
      const err = new Error(`[${breaker.name}] Circuit OPEN — request blocked`);
      err.circuitOpen = true;
      throw err;
    }

    const ac = new AbortController();
    const timer = setTimeout(() => ac.abort(), timeout);

    try {
      const r = await fetch(url, { ...fetchOpts, signal: ac.signal });
      clearTimeout(timer);

      if (!r.ok) {
        const err = new Error('HTTP ' + r.status);
        err.status = r.status;
        // Don't retry on 4xx client errors (except 429 rate limit)
        if (r.status >= 400 && r.status < 500 && r.status !== 429) {
          if (breaker) breaker.recordFailure();
          throw err;
        }
        throw err;
      }

      // Success
      if (breaker) breaker.recordSuccess();
      return r;

    } catch (e) {
      clearTimeout(timer);

      // Rewrite abort errors
      if (e.name === 'AbortError') {
        e = new Error('Timeout (' + Math.round(timeout / 1000) + 's): ' + url);
        e.isTimeout = true;
      }

      // Don't retry circuit-open errors
      if (e.circuitOpen) throw e;

      // Don't retry 4xx (except 429)
      if (e.status && e.status >= 400 && e.status < 500 && e.status !== 429) throw e;

      // Last attempt — record failure and throw
      if (attempt === maxAttempts) {
        if (breaker) breaker.recordFailure();
        throw e;
      }

      // Retry with backoff
      const delay = Math.min(backoffBase * Math.pow(2, attempt - 1), backoffMax);
      if (onRetry) {
        try { onRetry(attempt, e, delay); } catch (_) {}
      }
      await new Promise(r => setTimeout(r, delay));
    }
  }
}

// ===== PRE-CONFIGURED BREAKERS =====

/**
 * One breaker per external API source.
 * Each tuned to the API's characteristics:
 *   - TD: stricter (paid credits), longer cooldown
 *   - CG: lenient (free), short cooldown
 *   - FX: lenient, short cooldown
 *   - BIST: moderate (proxy dependency)
 *   - ServerAPI: moderate, checked separately
 */
export const breakers = {
  td: new CircuitBreaker('TwelveData', {
    failThreshold: 3,
    cooldownMs: 120000   // 2 min — save API credits
  }),
  cg: new CircuitBreaker('CoinGecko', {
    failThreshold: 5,
    cooldownMs: 30000    // 30s — free API, retry sooner
  }),
  fx: new CircuitBreaker('ExchangeRate', {
    failThreshold: 3,
    cooldownMs: 60000
  }),
  bist: new CircuitBreaker('BISTProxy', {
    failThreshold: 3,
    cooldownMs: 60000
  }),
  api: new CircuitBreaker('ServerAPI', {
    failThreshold: 2,
    cooldownMs: 300000   // 5 min — matches existing API_RETRY_MS
  }),
  ai: new CircuitBreaker('AIProvider', {
    failThreshold: 3,
    cooldownMs: 60000    // AI-1: back off 1 min after repeated provider/network failures
  })
};

/**
 * Get combined status of all breakers (for diagnostics UI)
 */
export function allBreakerStatus() {
  return Object.values(breakers).map(b => b.status());
}

/**
 * Reset all breakers (e.g., after settings change)
 */
export function resetAllBreakers() {
  Object.values(breakers).forEach(b => b.reset());
}
